import React, { useRef, useState } from "react";
import axios from "axios";
import { BsArrowLeft } from "react-icons/bs";
import { IoDocumentTextOutline, IoTrashOutline } from "react-icons/io5";
import { TbFileUpload } from "react-icons/tb";
import { MdCheck } from "react-icons/md";
import useLimits from "../../../hooks/useLimits";
import { SOURCE_OF_INCOME_OPTIONS } from "../../../constants/constants";
import { MONTHLY_INCOME_RANGE_OPTIONS } from "../../../constants/constants";
import useAuthAxios from "../../../utils/baseAxios";
import { API } from "../../../constants/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { errorMessage } from "../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import { useUser } from "../../../context/user-context";

const ACCEPT_IMAGE_OR_PDF =
  "image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf";

const isAllowedDocument = (file: File): boolean => {
  return file.type.startsWith("image/") || file.type === "application/pdf";
};

const Level3Flow = ({
  setOpenLevel3Flow,
  setOpenKyc,
  setOpenKycOnboarding,
}: {
  setOpenLevel3Flow: (open: boolean) => void;
  setOpenKyc?: (open: boolean) => void;
  setOpenKycOnboarding?: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();
  const { userDetails, refetch } = useUser();
  const email2fa = userDetails?.user?.account?.settings["2fa"]?.active;
  const google2fa =
    userDetails?.user?.account?.settings["2fa-auth-app"]?.active;
  const has2FA = !!email2fa || !!google2fa;
  const { limits } = useLimits();
  const LEVEL_3 = limits[2];
  // const TRADING_LEFT = LEVEL_3.tradingRaw - LEVEL_2.tradingRaw;
  // const WITHDRAWAL_LEFT = LEVEL_3.withdrawalRaw - LEVEL_2.withdrawalRaw;

  const [sourceOfIncome, setSourceOfIncome] = useState("");
  const [sourceOfIncomeOther, setSourceOfIncomeOther] = useState("");
  const [monthlyIncomeRange, setMonthlyIncomeRange] = useState("");
  const [employmentFile, setEmploymentFile] = useState<File | null>(null);
  const [employmentSignedUrl, setEmploymentSignedUrl] = useState("");
  const [employmentObjectUrl, setEmploymentObjectUrl] = useState("");
  const pendingEmploymentFileRef = useRef<File | null>(null);

  const toggleEmail2fa = async () => {
    const emailActive = !email2fa;
    const response = await axiosInstance.post(API.toggle2Fa(emailActive), {});
    return response.data;
  };
  const completeToggleMail = useMutation({
    mutationFn: toggleEmail2fa,
    onSuccess: (r: { message?: string }) => {
      toast.success(r.message ?? "2FA updated");
      refetch();
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const getUploadUrl = async (data: { documentType: string }) => {
    const response = await axiosInstance.post(API.getKyc4UploadURL, data);
    return response.data;
  };

  const getEmploymentUrl = useMutation({
    mutationFn: () => getUploadUrl({ documentType: "employment" }),
    onSuccess: (r: { data: { objectURL: string; signedURL: string } }) => {
      setEmploymentObjectUrl(r.data.objectURL);
      setEmploymentSignedUrl(r.data.signedURL);
      const file = pendingEmploymentFileRef.current;
      if (file) {
        uploadEmploymentFile.mutate({ file, signedUrl: r.data.signedURL });
        pendingEmploymentFileRef.current = null;
      }
    },
    onError: (e: any) => {
      pendingEmploymentFileRef.current = null;
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const uploadEmploymentFile = useMutation({
    mutationFn: async ({
      file,
      signedUrl,
    }: {
      file: File;
      signedUrl: string;
    }) => {
      await axios.put(signedUrl, file, {
        headers: { "Content-Type": file.type },
      });
    },
    onSuccess: () => {
      toast.success("Employment document uploaded");
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const handleEmploymentUpload = (file: File) => {
    setEmploymentFile(file);
    if (employmentSignedUrl) {
      uploadEmploymentFile.mutate({ file, signedUrl: employmentSignedUrl });
    } else {
      pendingEmploymentFileRef.current = file;
      getEmploymentUrl.mutate(undefined);
    }
  };

  const canSubmit =
    sourceOfIncome &&
    (sourceOfIncome !== "other" || sourceOfIncomeOther.trim()) &&
    monthlyIncomeRange &&
    employmentObjectUrl &&
    has2FA;

  const submitMutation = useMutation({
    mutationFn: (payload: {
      sourceOfIncome: string;
      monthlyIncomeRange: string;
      employmentDocumentUrl: string;
    }) => axiosInstance.post(API.submitKyc4, payload),
    onSuccess: () => {
      toast.success("Level 3 verification submitted.");
      queryClient.invalidateQueries({ queryKey: ["get-kyc-details"] });
      setOpenLevel3Flow(false);
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const handleSubmit = () => {
    if (!canSubmit) return;
    const sourceValue =
      sourceOfIncome === "other" ? sourceOfIncomeOther.trim() : sourceOfIncome;
    submitMutation.mutate({
      sourceOfIncome: sourceValue,
      monthlyIncomeRange,
      employmentDocumentUrl: employmentObjectUrl,
    });
  };

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3 flex flex-col max-h-[85vh] overflow-hidden">
        <div className="shrink-0 p-6 pb-0">
          <div className="w-full flex justify-start gap-6 items-center">
            <button type="button" onClick={() => setOpenLevel3Flow(false)}>
              <BsArrowLeft className="text-black dark:text-white text-[20px]" />
            </button>
            <h4 className="text-gray-900 dark:text-gray-100 font-sora font-medium text-[20px]">
              Level 3 Verification
            </h4>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-6 pt-4">
          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-2">
            What you'll unlock
          </h5>
          <div className="mt-3 rounded-xl border border-gray-200 bg-[#E8F3FC] dark:bg-[#1C2227] dark:border-gray-600 p-4">
            <div className="flex items-center gap-2 justify-between">
              <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[15px]">
                Level 3
              </h5>
              <span className=" text-gray-500 text-[11px] font-bold uppercase ">
                CURRENT LEVEL
              </span>
            </div>
            <div className="mt-3 space-y-4 bg-white dark:bg-[#1C2227] p-4 rounded-xl">
              <div className="flex justify-between border-b border-gray-200 dark:border-gray-600 pb-2 text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Trading Limit
                </span>
                <div className="flex flex-col">
                  <span className="text-gray-900 dark:text-gray-100 text-right font-bold text-[20px]">
                    <span className="text-[14px]">$</span>
                    {LEVEL_3.tradingRaw.toLocaleString()}
                  </span>
                  {/* <span className="text-gray-500 dark:text-gray-400 text-right mt-1 text-[12px]">
                      ${TRADING_LEFT.toLocaleString()} left
                    </span> */}
                </div>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Wallet Withdrawal
                </span>
                <div className="flex flex-col">
                  <span className="text-gray-900 dark:text-gray-100 text-right font-bold text-[20px]">
                    <span className="text-[14px]">$</span>
                    {LEVEL_3.withdrawalRaw.toLocaleString()}
                  </span>
                  {/* <span className="text-gray-500 dark:text-gray-400 text-right mt-1 text-[12px]">
                    ${WITHDRAWAL_LEFT.toLocaleString()} left
                  </span> */}
                </div>
              </div>
            </div>
          </div>

          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-6">
            Requirements
          </h5>
          <div className="mt-3 space-y-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  Employment Details
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                Current employment or business docs
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  Source of Income
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                Primary income declaration
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                      {has2FA ? "2FA Enabled" : "Enable 2FA"}
                    </h5>
                    {!has2FA && (
                      <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                        REQUIRED
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                    Mandatory for Level 3
                  </p>
                </div>
                {has2FA ? (
                  <MdCheck className="text-secondary_button text-[24px] shrink-0" />
                ) : (
                  <div
                    onClick={() => completeToggleMail.mutate()}
                    className={`flex w-[40px] cursor-pointer h-6 rounded-full transition-all duration-500 shrink-0 ${
                      email2fa ? "bg-secondary_button" : "bg-gray-600"
                    }`}
                  >
                    <span
                      className={`h-6 w-6 rounded-full transition-all duration-500 bg-gray-100 block ${
                        email2fa ? "ml-5" : ""
                      }`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
            <div className="flex items-center gap-3">
              <IoDocumentTextOutline className="text-gray-500 dark:text-gray-400 text-[24px] shrink-0" />
              <div className="min-w-0 flex-1">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  {employmentFile
                    ? employmentFile.name
                    : "Upload Employment Document"}
                </h5>
                <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5 truncate">
                  {employmentFile
                    ? "Change document"
                    : "Employment letter or business docs"}
                </p>
              </div>
              {employmentFile ? (
                <div className="shrink-0 flex items-center gap-2">
                  {uploadEmploymentFile.isPending ? (
                    <ClipLoader size={20} color="#0B75C2" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEmploymentFile(null);
                        setEmploymentObjectUrl("");
                        setEmploymentSignedUrl("");
                      }}
                      className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                      aria-label="Remove file"
                    >
                      <IoTrashOutline className="text-[18px]" />
                    </button>
                  )}
                </div>
              ) : (
                <label className="shrink-0 w-10 h-10 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-500 flex items-center justify-center cursor-pointer hover:border-secondary_button">
                  <TbFileUpload className="text-gray-500 dark:text-gray-400 text-[20px]" />
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_OR_PDF}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      if (!isAllowedDocument(f)) {
                        toast.error(
                          "Please upload an image (PNG, JPG, etc.) or PDF only."
                        );
                        return;
                      }
                      setEmploymentFile(f);
                      handleEmploymentUpload(f);
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
              Source of Income
            </label>
            <select
              value={sourceOfIncome}
              onChange={(e) => setSourceOfIncome(e.target.value)}
              className="mt-2 h-[44px] w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
            >
              <option value="">Select source of income</option>
              {SOURCE_OF_INCOME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {sourceOfIncome === "other" && (
              <input
                type="text"
                placeholder="Specify your source of income"
                value={sourceOfIncomeOther}
                onChange={(e) => setSourceOfIncomeOther(e.target.value)}
                className="mt-2 h-[44px] w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
              />
            )}
          </div>

          <div className="mt-4">
            <label className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
              Monthly Income Range
            </label>
            <select
              value={monthlyIncomeRange}
              onChange={(e) => setMonthlyIncomeRange(e.target.value)}
              className="mt-2 h-[44px] w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
            >
              <option value="">Select monthly income range</option>
              {MONTHLY_INCOME_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitMutation.isPending}
            className="w-full h-[52px] flex justify-center items-center font-manrope font-medium rounded-xl text-white bg-secondary_button mt-8"
          >
            {submitMutation.isPending ? (
              <ClipLoader color="#FFFFFF" size={30} />
            ) : has2FA ? (
              "Submit"
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Level3Flow;
